import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';

import {
  DiagnosticsCatalogResponseV1,
  DiagnosticsRunRequestV1,
  DiagnosticsRunResponseV1
} from '@core/models/diagnostics';
import { problemDetails, retryAfterSeconds } from '@core/utils/problem-details';
import { environment } from '@src/environments/environment';

/** The diagnostics API's base URL, or null where this environment's gateway does not publish it. */
export const DIAGNOSTICS_API_URL = new InjectionToken<string | null>('DIAGNOSTICS_API_URL', {
  providedIn: 'root',
  factory: () => (environment.apis.diagnostics ? `${environment.apiUrl}${environment.apis.diagnostics}/v1` : null)
});

export type DiagnosticsErrorKind =
  | 'invalid'
  | 'unauthenticated'
  | 'forbidden'
  | 'notFound'
  | 'notInEnvironment'
  | 'notDeployed'
  | 'rateLimited'
  | 'timeout'
  | 'queryFailed'
  | 'unavailable';

/** The kinds where the same request can succeed a little later. */
const RETRYABLE_KINDS: ReadonlySet<DiagnosticsErrorKind> = new Set<DiagnosticsErrorKind>([
  'rateLimited',
  'timeout',
  'queryFailed',
  'unavailable'
]);

/** A failed diagnostics call, as the page presents it: a headline, the server's detail, and whether to offer a retry. */
export class DiagnosticsError extends Error {
  constructor(
    readonly kind: DiagnosticsErrorKind,
    readonly status: number,
    message: string,
    readonly detail: string | null
  ) {
    super(message);
    this.name = 'DiagnosticsError';
  }

  get retryable(): boolean {
    return RETRYABLE_KINDS.has(this.kind);
  }
}

/** A 404 on the catalog means this environment's gateway has no diagnostics API; on a run, no such query. */
export type DiagnosticsCall = 'catalog' | 'run';

export function toDiagnosticsError(error: unknown, call: DiagnosticsCall = 'run'): DiagnosticsError {
  if (error instanceof DiagnosticsError) {
    return error;
  }
  if (!(error instanceof HttpErrorResponse)) {
    return new DiagnosticsError('unavailable', 0, 'Diagnostics service unavailable.', null);
  }

  const problem = problemDetails(error);
  const detail = problem.detail ?? null;
  const status = error.status;

  switch (status) {
    case 400:
      return new DiagnosticsError('invalid', status, 'The query was rejected.', detail ?? problem.title ?? null);
    case 401:
      return new DiagnosticsError('unauthenticated', status, 'Your session has expired.', detail);
    case 403:
      return new DiagnosticsError('forbidden', status, 'Diagnostics refused this request.', detail);
    case 404:
      return call === 'catalog'
        ? new DiagnosticsError('notInEnvironment', status, 'Diagnostics are not available in this environment.', detail)
        : new DiagnosticsError('notFound', status, 'This database or query is not in the catalog.', detail);
    case 409:
      return new DiagnosticsError('notDeployed', status, 'This query is not deployed in this environment.', detail);
    case 429: {
      const seconds = retryAfterSeconds(error);
      const wait = seconds === null ? detail : `Try again in ${seconds} ${seconds === 1 ? 'second' : 'seconds'}.`;
      return new DiagnosticsError('rateLimited', status, 'Too many diagnostics requests.', wait);
    }
    case 502:
      return new DiagnosticsError('queryFailed', status, 'The query failed on the server.', detail);
    case 504:
      return new DiagnosticsError(
        'timeout',
        status,
        'The query timed out.',
        detail ?? 'Narrow it with filters or a lower row limit, then retry.'
      );
  }

  // Below 400 is a response that failed to parse, such as a gateway or dev-server HTML page: nothing answered.
  if (status < 400 || status >= 500) {
    return new DiagnosticsError('unavailable', status, 'Diagnostics service unavailable.', detail);
  }
  return new DiagnosticsError('invalid', status, 'The query was rejected.', detail ?? problem.title ?? null);
}

@Injectable({
  providedIn: 'root'
})
export class DiagnosticsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(DIAGNOSTICS_API_URL);

  /** Every diagnostic query available in this environment, grouped by database. */
  getCatalog(): Observable<DiagnosticsCatalogResponseV1> {
    return this.http
      .get<DiagnosticsCatalogResponseV1>(`${this.apiUrl}/catalog`)
      .pipe(catchError((error) => this.errorHandler(error, 'catalog', 'Error getting the diagnostics catalog')));
  }

  run(request: DiagnosticsRunRequestV1): Observable<DiagnosticsRunResponseV1> {
    return this.http
      .post<DiagnosticsRunResponseV1>(`${this.apiUrl}/run`, request)
      .pipe(
        catchError((error) => this.errorHandler(error, 'run', `Error running ${request.database}/${request.name}`))
      );
  }

  /** The page renders each failure where it happened, so this reports to the caller instead of raising a toast. */
  private errorHandler(error: unknown, call: DiagnosticsCall, serviceMessage: string): Observable<never> {
    const failure = toDiagnosticsError(error, call);
    console.error(`[DiagnosticsService] ${serviceMessage}, ${failure.status} ${failure.message}`);
    return throwError(() => failure);
  }
}
